import { Link, useLoaderData, useRouteLoaderData, type LoaderFunctionArgs } from "react-router";
import type { Change, Review } from "@edm/contracts";
import { barLength, Loading, TableSkeleton } from "../../components/skeletons.jsx";
import { Deferred, EmptyState } from "../../components/states.jsx";
import { useI18n } from "../../i18n/index.js";
import type { WorkspaceApi } from "../../lib/api.js";
import { deferred } from "../../lib/deferred.js";
import { apiContext } from "../../lib/session.js";
import { sessionContext } from "../../lib/session.js";
import { workspaceContext } from "../../lib/workspace.js";
import { Badge, Card, ChangeIcon, LinkButton, PageHeader, Skeleton, StatusBadge, Table } from "../../ui/index.js";
import type { workspaceLayoutLoader } from "./layout.jsx";

/**
 * My Changes: whether your own pull requests met what is on record.
 *
 * A list of pull requests alone would not earn a place in the sidebar — GitHub
 * already carries the badge. What earns it is the pattern across them: hitting
 * the same rule three times is either a habit or a rule scoped too widely, and
 * asking which is how a wrong rule gets found before it erodes trust.
 */

type Finding = Review["findings"][number];

const SCAN_LIMIT = 20;
const REPEAT_THRESHOLD = 3;

async function fallback<T>(p: Promise<T>, value: T): Promise<T> {
  try {
    return await p;
  } catch {
    return value;
  }
}

export function myChangesLoader({ context }: LoaderFunctionArgs) {
  const ws = context.get(workspaceContext);
  const me = context.get(sessionContext)!;
  return { myChanges: deferred(loadMyChanges(context.get(apiContext).workspace(ws.id), me.actor.id)) };
}

async function loadMyChanges(api: WorkspaceApi, meId: string) {
  const all = await fallback(api.listChanges(), { items: [] as Change[], nextCursor: null });

  const mine = all.items.filter((c) => c.createdBy.includes(meId) || c.authorActor === meId).slice(0, SCAN_LIMIT);
  const reviews = await Promise.all(mine.map((c) => fallback(api.changeReview(c.id), null).then((review) => ({ change: c, review }))));

  // Which rule keeps coming up across this person's own changes.
  const hits = new Map<string, { count: number; statement: string }>();
  for (const { review } of reviews) {
    for (const f of (review?.findings ?? []) as Finding[]) {
      for (const id of f.constraintIds ?? []) {
        const seen = hits.get(id);
        hits.set(id, { count: (seen?.count ?? 0) + 1, statement: seen?.statement || f.explanation });
      }
    }
  }
  const repeated = [...hits.entries()]
    .filter(([, v]) => v.count >= REPEAT_THRESHOLD)
    .sort((a, b) => b[1].count - a[1].count)
    .slice(0, 1)
    .map(([id, v]) => ({ id, ...v }));

  return {
    changes: reviews.map(({ change, review }) => ({
      change,
      findings: ((review?.findings ?? []) as Finding[]).filter((f) => (f.status ?? "suggested") === "suggested").length,
      conforms: ((review?.findings ?? []) as Finding[]).filter((f) => f.class === "conformance").length,
      reviewed: review !== null,
    })),
    repeated,
    hasAny: all.items.length > 0,
  };
}

function ChangesHead() {
  const { t } = useI18n();
  return (
    <thead>
      <tr>
        <th>{t("changes.th.change")}</th>
        <th>{t("changes.th.scope")}</th>
        <th>{t("changes.th.status")}</th>
      </tr>
    </thead>
  );
}

function ChangesTableSkeleton() {
  return (
    <Loading>
      <Card padded={false}>
        <TableSkeleton head={<ChangesHead />} cells={(i) => [<Skeleton width={barLength(i)} />, <Skeleton width={96} />, <Skeleton shape="badge" width={88} />]} />
      </Card>
    </Loading>
  );
}

export function MyChangesSkeleton() {
  const { t } = useI18n();
  return (
    <>
      <PageHeader title={t("myChanges.title")} description={t("myChanges.description")} />
      <ChangesTableSkeleton />
    </>
  );
}

export function MyChangesRoute() {
  const { myChanges } = useLoaderData<typeof myChangesLoader>();
  const { t } = useI18n();

  return (
    <>
      <PageHeader title={t("myChanges.title")} description={t("myChanges.description")} />
      <Deferred data={myChanges} fallback={<ChangesTableSkeleton />}>
        {(data) => <MyChanges {...data} />}
      </Deferred>
    </>
  );
}

function MyChanges({ changes, repeated, hasAny }: Awaited<ReturnType<typeof loadMyChanges>>) {
  const { workspace } = useRouteLoaderData("workspace") as ReturnType<typeof workspaceLayoutLoader>;
  const { t } = useI18n();
  const base = `/w/${workspace.slug}`;

  return (
    <>
      {changes.length === 0 ? (
        <EmptyState
          title={t("myChanges.empty.title")}
          hint={t("myChanges.empty.hint")}
          icon={<ChangeIcon size={18} />}
          {...(hasAny ? {} : { action: <LinkButton to={`${base}/settings/api-keys`}>{t("myChanges.empty.action")}</LinkButton> })}
        />
      ) : (
        <Card padded={false}>
          <Table>
            <ChangesHead />
            <tbody>
              {changes.map(({ change, findings, conforms, reviewed }) => (
                <tr key={change.id}>
                  <td className="dt-table__primary">
                    <Link to={`${base}/changes/${change.id}`}>{change.title}</Link>
                  </td>
                  <td className="dt-muted">{change.scope ?? ""}</td>
                  <td>
                    {!reviewed ? (
                      <StatusBadge status={change.status} />
                    ) : findings > 0 ? (
                      <Badge tone="danger">{t("myChanges.badge.conflict", { count: findings })}</Badge>
                    ) : conforms > 0 ? (
                      <Badge tone="success">{t("myChanges.badge.clean", { count: conforms })}</Badge>
                    ) : (
                      <Badge tone="neutral">{t("myChanges.badge.noFindings")}</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {/* The rule might be wrong, not the person. Both answers are offered. */}
      {repeated.map((r) => (
        <Card key={r.id} title={t("myChanges.pattern.title", { count: r.count })}>
          <p>{t("myChanges.pattern.body", { id: r.id, statement: r.statement })}</p>
          <p className="dt-muted">{t("myChanges.pattern.question")}</p>
          <div className="dt-inline dt-gap-2">
            <LinkButton to={`${base}/decisions`} variant="secondary">
              {t("myChanges.pattern.amend")}
            </LinkButton>
          </div>
        </Card>
      ))}
    </>
  );
}
